import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { FiArrowRight, FiLock, FiClock, FiStar, FiShield, FiCheck, FiMail, FiHeart } from 'react-icons/fi'
import { HiAcademicCap } from 'react-icons/hi2'
import { LookupStudentService } from '../../services/TeacherFeedbackServices'
import { Hero, Card, PrimaryButton, BRAND_NAVY, BRAND_GOLD } from './components/PublicHeader'

export default function PublicFeedbackMobileEntry() {
  const navigate = useNavigate()
  const [mobile, setMobile] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // Set once the mobile number is looked up successfully — the parent then
  // sees who they're about to give feedback for and must confirm before
  // moving on, instead of the old flow that jumped straight to the next page.
  const [student, setStudent] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  async function handleLookup(e) {
    e.preventDefault()
    setError('')

    if (!/^[0-9]{10}$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number')
      return
    }

    setLoading(true)
    const result = await LookupStudentService(mobile)
    setLoading(false)

    if (result?.data?.status) {
      setStudent(result.data.data)
    } else {
      setError(result?.data?.message || 'No student found for this mobile number. Please check and try again.')
      toast.error(result?.data?.message || 'Student not found')
    }
  }

  function handleContinue() {
    sessionStorage.setItem('feedback_student', JSON.stringify(student))
    navigate('/parent_feedback/teachers')
  }

  function handleChangeNumber() {
    setStudent(null)
    setMobile('')
    setError('')
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div className="min-h-screen bg-white">
      <Hero icon={HiAcademicCap} title="We Value Your Feedback" subtitle="Your feedback helps us improve your child's learning experience and recognize outstanding teachers." />

      <div className="max-w-md mx-auto px-4 -mt-4 pb-14 relative">
        <Card>
          <div className="text-center mb-5">
            <p className="font-bold text-gray-800 text-[16px]">Let's get started</p>
            <p className="text-gray-400 text-[12.5px] mt-1">
              Enter the mobile number registered with your child's account.
            </p>
          </div>

          <form onSubmit={student ? (e) => e.preventDefault() : handleLookup}>
            <label className="block text-[10.5px] font-bold text-gray-400 uppercase tracking-wider mb-2">Mobile Number</label>
            <div
              className={`flex items-center border-2 rounded-xl overflow-hidden transition-colors bg-white ${error ? 'border-red-300' : student ? 'border-[#1a9d5c]' : 'border-gray-200 focus-within:border-[#033ebd]'}`}
            >
              <span className="pl-4 pr-3 py-[10px] text-gray-400 font-semibold text-[15px] border-r border-gray-200 select-none bg-gray-50/60">+91</span>
              <input
                ref={inputRef}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={mobile}
                disabled={!!student}
                onChange={(e) => { setMobile(e.target.value.replace(/\D/g, '')); setError('') }}
                placeholder="98765 43210"
                className="w-full px-3.5 py-[10px] text-[16px] tracking-wide outline-none bg-transparent disabled:text-gray-700"
              />
              {student && (
                <span className="pr-4 flex-shrink-0">
                  <FiCheck className="text-white rounded-full p-0.5" style={{ background: '#1a9d5c' }} size={18} />
                </span>
              )}
            </div>
            {error && <p className="text-red-500 text-[12.5px] mt-2 font-medium">{error}</p>}

            {student && (
              <div className="mt-4 rounded-xl border p-3.5" style={{ background: '#fafffc', borderColor: '#cfefdd' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full capitalize flex items-center justify-center text-[15px] font-bold text-white flex-shrink-0" style={{ background: BRAND_NAVY }}>
                    {student.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold capitalize text-gray-900 text-[14px] truncate">{student.name}</p>
                    <div className="flex items-center gap-2.5 mt-0.5 flex-wrap">
                      {student.standard && (
                        <span className="text-[11px] text-gray-500 flex items-center gap-1"><HiAcademicCap size={12} /> {student.standard}</span>
                      )}
                      {student.mobile && <span className="text-[11px] text-gray-500">+91 {student.mobile}</span>}
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 text-white" style={{ background: '#1a9d5c' }}>
                    <FiCheck size={11} /> Verified
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#c9ecd8] flex-wrap gap-1.5">
                  <p className="text-[10.5px] text-gray-500 flex items-center gap-1"><FiShield size={11} className="flex-shrink-0" /> Please confirm this is the correct student.</p>
                  <button type="button" onClick={handleChangeNumber} className="text-[11px] font-bold flex-shrink-0" style={{ color: BRAND_NAVY }}>
                    Not my child? Change
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6">
              {student ? (
                <PrimaryButton type="button" onClick={handleContinue}>
                  Continue <FiArrowRight />
                </PrimaryButton>
              ) : (
                <PrimaryButton type="submit" loading={loading}>
                  {loading ? 'Checking…' : <>Continue <FiArrowRight /></>}
                </PrimaryButton>
              )}
            </div>
          </form>

          <div className="flex items-start gap-2.5 mt-6 pt-5 border-t border-gray-100">
            <FiLock className="text-gray-300 mt-0.5 flex-shrink-0" />
            <p className="text-[11.5px] text-gray-400 leading-snug">
              Your feedback is <span className="font-semibold" style={{ color: BRAND_NAVY }}>confidential</span> and used only to improve teaching quality.
            </p>
          </div>
        </Card>

        <div className="grid grid-cols-3 divide-x divide-gray-100 mt-5">
          <TrustStat icon={FiClock} iconBg="#e8f0fe" iconColor="#3b6fd9" title="Takes less than 2 minutes" caption="Quick & Easy" />
          <TrustStat icon={FiShield} iconBg="#f1e9fb" iconColor="#8347c9" title="100% Confidential" caption="Safe & Secure" />
          <TrustStat icon={FiStar} iconBg="#fdf1de" iconColor="#d1861f" title="5 Simple Questions" caption="Straightforward" />
        </div>

        <div className="rounded-2xl px-4 py-4 mt-4 flex items-center gap-3.5" style={{ background: '#e9ecfa' }}>
          <span className="relative flex-shrink-0">
            <FiMail size={34} strokeWidth={1.6} style={{ color: BRAND_NAVY }} />
            <span className="absolute -top-1 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#fdeaec' }}>
              <FiHeart size={8} className="text-[#e0658a]" fill="#e0658a" />
            </span>
          </span>
          <div>
            <p className="text-[13px] text-gray-600 leading-snug">
              Thank you for helping us maintain <span className="font-extrabold" style={{ color: BRAND_NAVY }}>high teaching standards.</span>
            </p>
            <div className="w-8 h-[3px] rounded-full mt-1.5" style={{ background: BRAND_GOLD }} />
          </div>
        </div>
      </div>
    </div>
  )
}

function TrustStat({ icon: Icon, iconBg, iconColor, title, caption }) {
  return (
    <div className="text-center px-1.5">
      <span className="mx-auto mb-2 w-11 h-11 rounded-full flex items-center justify-center" style={{ background: iconBg }}>
        <Icon style={{ color: iconColor }} size={18} />
      </span>
      <p className="text-[11px] font-extrabold leading-tight" style={{ color: iconColor }}>{title}</p>
      <p className="text-[9.5px] text-gray-400 mt-1 leading-tight">{caption}</p>
    </div>
  )
}
